import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  LayoutDashboard, Ticket, Users, AppWindow, Activity,
  Bell, LogOut, ChevronLeft, ChevronRight, Shield, Timer, ListChecks, UploadCloud, Truck, FlaskConical, MonitorPlay
} from 'lucide-react';
import logo from '../../assets/jeta-logo.png';
import '../../styles/businessTheme.css';
import './Layout.css';

const navItems = [
  { to: '/dashboard',     icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/tickets',       icon: Ticket,          label: 'Tickets' },
  { to: '/observability', icon: Activity,        label: 'Observability' },
  { to: '/clients',       icon: Users,           label: 'Clients',       adminOnly: true },
  { to: '/notifications', icon: Bell,            label: 'Notifications', adminOnly: true },
  { to: '/sla',           icon: Timer,           label: 'SLA Policies',  adminOnly: true },
  { to: '/trials',        icon: FlaskConical,    label: 'Trial Requests', adminOnly: true },
  { to: '/demos',         icon: MonitorPlay,     label: 'Demo Requests',  adminOnly: true },
];

// Separate Business Support vertical (Task 11) — only shown for business_support_lead/viewer
// (and superadmin, who sees both verticals). Existing items and their visibility above are untouched.
const businessNavItems = [
  { to: '/business/dashboard',    icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/business/tickets',      icon: Ticket,          label: 'Tickets' },
  { to: '/business/checklist',    icon: ListChecks,      label: 'Checklist' },
  { to: '/business/suppliers',    icon: Truck,           label: 'Suppliers' },
  { to: '/business/admin/import', icon: UploadCloud,     label: 'Import Tracker', leadOnly: true },
];

// Pure business roles never touch the technical module — they should see only their own
// nav, not the technical items too. superadmin still sees both (isBusinessSupport true,
// isPureBusinessRole false), which is what keeps its access to everything.
const PURE_BUSINESS_ROLES = ['business_support_lead', 'business_support_viewer'];

export default function Layout({ children }) {
  const { user, logout, isAdmin, isBusinessSupport, isBusinessLead } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isPureBusinessRole = PURE_BUSINESS_ROLES.includes(user?.role);
  // Chrome (sidebar/topbar) follows the light theme on business routes and reverts to
  // dark everywhere else — this only swaps the CSS variable palette (see businessTheme.css),
  // it doesn't touch layout/sizing, so it's safe to apply to the shared chrome directly.
  const isBusinessRoute = location.pathname.startsWith('/business');

  return (
    <div className={`layout ${collapsed ? 'layout--collapsed' : ''} ${isBusinessRoute ? 'biz-theme' : ''}`}>
      <aside className="sidebar">
        <div className="sidebar__logo">
          <Shield size={22} color="var(--accent)" />
          {!collapsed && <span>SupportOps</span>}
        </div>
        <nav className="sidebar__nav">
          {!isPureBusinessRole && navItems.filter(i => !i.adminOnly || isAdmin).map(({ to, icon: Icon, label }) => (
            <NavLink key={to} to={to} className={({ isActive }) => `nav-item ${isActive ? 'nav-item--active' : ''}`}>
              <Icon size={18} />
              {!collapsed && <span>{label}</span>}
            </NavLink>
          ))}
          {isBusinessSupport && (
            <>
              {!collapsed && !isPureBusinessRole && <div className="sidebar__section-label">Business Support</div>}
              {businessNavItems.filter(i => !i.leadOnly || isBusinessLead).map(({ to, icon: Icon, label }) => (
                <NavLink key={to} to={to} className={({ isActive }) => `nav-item ${isActive ? 'nav-item--active' : ''}`}>
                  <Icon size={18} />
                  {!collapsed && <span>{label}</span>}
                </NavLink>
              ))}
            </>
          )}
        </nav>
        <div className="sidebar__footer">
          <button className="nav-item nav-item--danger" onClick={() => { logout(); navigate('/login'); }}>
            <LogOut size={18} />
            {!collapsed && <span>Logout</span>}
          </button>
        </div>
        <button className="sidebar__toggle" onClick={() => setCollapsed(c => !c)}>
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      </aside>
      <div className="main">
        <header className="topbar">
          <div className="topbar__brand">
            <img src={logo} alt="JetaTech" className="topbar__logo" />
          </div>
          <div className="topbar__right">
            <div className="topbar__user">
              <div className="avatar">{user?.name?.[0]?.toUpperCase()}</div>
              <div>
                <div className="topbar__name">{user?.name}</div>
                <div className="topbar__role">{user?.role?.replace(/_/g,' ')}</div>
              </div>
            </div>
          </div>
        </header>
        <main className="content">{children}</main>
      </div>
    </div>
  );
}
